import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUpdateRoleMutation } from "@/hooks/useUsers";
import type { UserRole, UserWithRole } from "@/types/users";
import { ROLE_LABELS } from "@/types/users";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

interface EditRoleDialogProps {
  user: UserWithRole | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditRoleDialog({ user, open, onOpenChange }: EditRoleDialogProps) {
  const [role, setRole] = useState<UserRole>("installer");
  const [partnerId, setPartnerId] = useState<string>("");

  const updateMutation = useUpdateRoleMutation();

  // Hae partnerit
  const { data: partners = [] } = useQuery({
    queryKey: ["partners-for-select"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partners")
        .select("id, name")
        .is("deleted_at", null)
        .order("name");

      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  // Alusta arvot käyttäjän tiedoista
  useEffect(() => {
    if (user && open) {
      setRole(user.role || "installer");
      setPartnerId(user.partner_id || "");
    }
  }, [user, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;

    await updateMutation.mutateAsync({
      userId: user.id,
      role,
      partnerId: role === "partner" ? partnerId : null,
    });

    onOpenChange(false);
  };

  const isValid = role !== "partner" || partnerId;
  const userName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Muokkaa käyttäjän roolia</DialogTitle>
            <DialogDescription>{userName}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-role">Rooli</Label>
              <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {role === "partner" && (
              <div className="grid gap-2">
                <Label htmlFor="edit-partner">Partner-organisaatio *</Label>
                <Select value={partnerId} onValueChange={setPartnerId}>
                  <SelectTrigger id="edit-partner">
                    <SelectValue placeholder="Valitse partneri..." />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map((partner) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Peruuta
            </Button>
            <Button type="submit" disabled={!isValid || updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Tallenna
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
